import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MenuPreviewPanel from "./MenuPreviewPanel";
import { ArrowLeft, Save, RefreshCw } from "lucide-react";
import axios from "axios";
import { message } from "antd";
import API_BASE from "../config/api";
import { DEFAULT_DESIGN_OPTIONS } from "./MenuPreviewPanel";
import {
  sanitizeDesignOptionsForSave,
  cacheDesignOptions,
  loadCachedDesignOptions,
} from "../utils/menuDesignOptions";

const MenuPreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // All data comes from navigation state — no useAIMenu() here
  const passedSections = location.state?.sections || [];
  console.log("sections", passedSections)
  const passedSelectedSectionId = location.state?.selectedSectionId || null;
  // The active store's own customer_id (see useAIMenu.js / getMerchantStoreDetails)
  // — used for /updateCustomerMenu, the local caches, and MenuPreviewPanel's
  // own background-image / publish-to-mode calls.
  const passedCustomerId =
    location.state?.customerId ||
    localStorage.getItem("customer_id") ||
    null;
  const cachedDesign = passedCustomerId
    ? loadCachedDesignOptions(passedCustomerId)
    : null;
  const passedDesignOptions =
    location.state?.designOptions ||
    (cachedDesign
      ? { ...DEFAULT_DESIGN_OPTIONS, ...cachedDesign }
      : DEFAULT_DESIGN_OPTIONS);

  const [designOptions, setDesignOptions] = useState(passedDesignOptions);
  const [isSyncing, setIsSyncing] = useState(false);

  const formatPrice = (value) => {
    const raw = String(value ?? "").trim().replace(/^\$/, "");
    if (!raw) return "";
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num === 0) return raw.startsWith("$") ? raw : `$${raw}`;
    return `$${num.toFixed(2)}`;
  };

  const persistMenu = async (designOptsOverride, options = {}) => {
    const { silent = false } = options;
    const cid = passedCustomerId;
    if (!cid) {
      if (!silent) message.warning("Please log in to save.");
      return;
    }

    const activeDesignOptions = designOptsOverride || designOptions;
    if (designOptsOverride) {
      setDesignOptions(designOptsOverride);
    }

    setIsSyncing(true);

    const totalItems = passedSections.reduce(
      (sum, s) => sum + (s.items?.length || 0),
      0,
    );

    let safeDesignOpts = sanitizeDesignOptionsForSave(activeDesignOptions);

    const payloadProbe = JSON.stringify({
      menu: {
        menu: {
          sections: (passedSections || []).map((s) => ({
            id: s.id,
            name: s.name,
            items: s.items || [],
          })),
        },
        designOptions: safeDesignOpts,
      },
    });

    if (payloadProbe.length > 60000 && safeDesignOpts.backgroundImageThumb) {
      safeDesignOpts = { ...safeDesignOpts, backgroundImageThumb: undefined };
    }

    const payload = {
      customerId: cid,
      menu: {
        menu: {
          sections: passedSections.map((s) => ({
            id: s.id,
            name: s.name,
            items: s.items || [],
          })),
        },
        metadata: {
          generatedAt: new Date().toISOString(),
          totalSections: passedSections.length,
          totalItems: totalItems,
        },
        designOptions: safeDesignOpts,
      },
    };

    try {
      await axios.post(`${API_BASE}/updateCustomerMenu`, payload);
      localStorage.setItem(`ai_menu_${cid}`, JSON.stringify(passedSections));
      cacheDesignOptions(cid, activeDesignOptions);
      if (!silent) {
        message.success({
          content: "Menu saved successfully.",
          key: "menu-saved-success",
        });
      }
    } catch (err) {
      console.error("Save error:", err);
      if (!silent) {
        const backendMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unknown error";
        message.error({
          content: `Save failed: ${backendMessage}`,
          key: "menu-save-failed",
        });
      }
      throw err;
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSave = () => persistMenu();

  const visibleSections = passedSections.map((s) => ({
    ...s,
    items: (s.items || []).filter((i) => i.isVisible !== false),
  }));

  return (
    <div className="flex flex-col min-h-[100dvh] w-full min-w-0 overflow-x-hidden bg-gray-50 pb-6">

      {/* ── Header ───────────────────────────────────── */}
      <div className="sticky top-0 z-10 bg-white px-3 sm:px-4 pt-3 sm:pt-4 pb-3 flex items-center justify-between gap-2 shadow-sm border-b border-gray-100">

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs sm:text-sm text-gray-600 border border-gray-200 px-2.5 sm:px-3 py-1.5 rounded-full flex-shrink-0 touch-manipulation"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="text-center min-w-0 flex-1 px-1">
          <h2 className="text-sm sm:text-lg font-bold text-gray-900 truncate">
            Preview &amp; Publish
          </h2>
          <p className="text-xs text-gray-500 hidden sm:block">
            Select screen and design, then save.
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={isSyncing || !passedCustomerId}
          className="flex items-center gap-1 bg-blue-600 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full disabled:opacity-50 flex-shrink-0 touch-manipulation"
        >
          {isSyncing ? (
            <RefreshCw size={13} className="animate-spin" />
          ) : (
            <Save size={13} />
          )}
          Save
        </button>
      </div>

      {/* ── MenuPreviewPanel ─────────────────────────── */}
      <div className="flex-1 overflow-x-hidden overflow-y-auto w-full min-w-0">
        <MenuPreviewPanel
          sections={visibleSections}
          customerId={passedCustomerId}
          formatPrice={formatPrice}
          setSections={() => { }}
          selectedSectionId={passedSelectedSectionId}
          designOptions={designOptions}
          onDesignOptionsChange={setDesignOptions}
          onPersistMenu={(opts) => persistMenu(opts, { silent: true })}
          embedMode={true}
        />
      </div>

    </div>
  );
};

export default MenuPreviewPage;
